import React, {useState, useMemo, useEffect, useRef, useCallback} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box, styled} from '@mui/material';
import {
  Button,
  designSystemToken,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import {Avatar} from '@contentlab/api/avatars';
import DismissibleAlert from '@contentlab/components/dismissible-alert';
import ReplaceVideoButton from '@contentlab/components/replace-video-button';
import ReplaceVideoModal from '@contentlab/components/replace-video-modal';
import VideoDisplay, {
  PlayToggleType,
} from '@contentlab/components/video-display';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useViewTransitionEvent from '@contentlab/hooks/analytics/use-view-transition-event';
import useTtsPreview from '@contentlab/hooks/use-tts-preview/useTtsPreview';
import contentLabStore, {
  contentLabActions,
  UploadedVideo,
  UploadingFile,
} from '@contentlab/stores/contentLabStore';
import {AvatarKind} from '@contentlab/types/aiAvatars';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import useBrandId from '@/hooks/use-brand-id';
import {AiAvatarVideoTitle} from './AiAvatarVideoTitle';
import AudioMixPopper from './AudioMixPopper';
import AvatarOverlay from './AvatarOverlay';

const BUFFER_CHUNKS = 20;

const OverlayAlertContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'isVisible',
})<{width: string; isVisible: boolean}>(({width, isVisible}) => ({
  position: 'absolute',
  bottom: '78px',
  left: '50%',
  transform: 'translateX(-50%)',
  width,
  zIndex: 10,
  opacity: isVisible ? 1 : 0,
  transition: 'opacity 300ms ease-in-out',
  pointerEvents: isVisible ? 'auto' : 'none',
}));

interface AiAvatarVideoDisplayProps {
  video: UploadedVideo;
  asset?: Asset;
  uploadingFiles: Record<string, UploadingFile>;
  createDropzoneConfig: (() => object) | null;
  selectedAvatar: Avatar | null;
  projectId: string;
  isScriptEdited?: boolean;
  handleAudioMixChange: (
    newAvatarAudioReductionFactor: number,
    newBackgroundAudioReductionFactor: number
  ) => void;
  showAudioMixer: boolean;
}

function AiAvatarVideoDisplay({
  video,
  asset,
  uploadingFiles,
  createDropzoneConfig,
  selectedAvatar,
  projectId,
  isScriptEdited = false,
  handleAudioMixChange,
  showAudioMixer,
}: AiAvatarVideoDisplayProps) {
  const brandId = useBrandId();
  const [showReplaceVideoModal, setShowReplaceVideoModal] = useState(false);
  const [replaceVideoModalPresentationId, setReplaceVideoModalPresentationId] =
    useState<string>(uuid());
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isAvatarAnimationAlertDismissed, setIsAvatarAnimationAlertDismissed] =
    useState(false);
  const shouldPlayWhenBufferCompleteRef = useRef(false);
  const [audioMixPopperOpen, setAudioMixPopperOpen] = useState(false);
  const [audioMixAnchorEl, setAudioMixAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const avatarAlertIdsRef = useRef<{
    presentationId: string | null;
    interactionId: string | null;
  }>({
    presentationId: null,
    interactionId: null,
  });

  const {uiInteraction} = useUiInteractionEvent();
  const {viewPresented} = useViewPresentedEvent();
  const {
    presented: viewTransitionPresented,
    dismissed: viewTransitionDismissed,
  } = useViewTransitionEvent();
  const uploadedFile = uploadingFiles[video?.fileName || ''];

  const {isMuted, avatarSpec, selectedAvatarKind, scriptData} = contentLabStore(
    useShallow((state) => ({
      isMuted: state.isMuted,
      avatarSpec: state.draftAiAvatarProject.avatarSpec,
      selectedAvatarKind: state.draftAiAvatarProject.selectedAvatarKind,
      scriptData: state.draftAiAvatarProject.scriptData,
    }))
  );

  const tts = useTtsPreview({
    volume: isMuted ? 0 : avatarSpec.avatarAudioReductionFactor,
    onPlayStart: () => {
      setIsVideoPlaying(true);
    },
    onBufferComplete: () => {
      if (shouldPlayWhenBufferCompleteRef.current && voiceId) {
        shouldPlayWhenBufferCompleteRef.current = false;
        setIsVideoPlaying(true);
        // Play the audio from the beginning
        tts.preview({
          text: currentScriptText,
          voice_id: voiceId,
          project_id: projectId,
          brand_id: brandId,
        });
      }
    },
    abortOnStop: false,
    bufferChunks: BUFFER_CHUNKS,
  });

  const currentScriptText = useMemo(() => {
    const idx = scriptData.selectedScriptIndex;
    if (idx == null) return '';
    const script = scriptData.scripts?.[idx];
    return script?.script || '';
  }, [scriptData]);

  const voiceId = useMemo(() => {
    if (selectedAvatarKind === AvatarKind.LIBRARY) {
      return selectedAvatar?.voiceId || null;
    }
    if (selectedAvatarKind === AvatarKind.VOICE_ONLY) {
      return avatarSpec.voiceIdVoiceOnly || selectedAvatar?.voiceId || null;
    }
    return avatarSpec.voiceId || null;
  }, [selectedAvatarKind, selectedAvatar, avatarSpec]);

  // Check if scripts have been generated
  const hasGeneratedScripts = useMemo(() => {
    return scriptData.scripts.length > 0;
  }, [scriptData.scripts]);

  const isAvatarAlertVisible =
    isVideoPlaying &&
    !isAvatarAnimationAlertDismissed &&
    hasGeneratedScripts &&
    selectedAvatarKind === AvatarKind.LIBRARY;

  const generateVideoFileUrl = () => {
    if (uploadedFile) {
      return URL.createObjectURL(uploadedFile.file);
    }
    return '';
  };

  const videoProps = {
    videoName: (
      <AiAvatarVideoTitle
        hasGeneratedScripts={hasGeneratedScripts}
        selectedAvatar={selectedAvatar}
        fallbackName={asset?.name || video?.fileName || ''}
      />
    ),
    videoUrl: asset?.url || generateVideoFileUrl(),
    subtitleTime: 0,
  };

  const onPlayToggle = (isPlaying: boolean, type: PlayToggleType) => {
    if (type === PlayToggleType.UNCONTROLLED) {
      return;
    }

    uiInteraction({
      item_name: 'ai_avatar_video_play',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
      interaction_details: isPlaying ? 'pause' : 'play',
      flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
    });

    if (audioMixPopperOpen) {
      setAudioMixPopperOpen(false);
    }

    // Handle TTS when script and voice are available
    if (currentScriptText && voiceId) {
      if (isPlaying) {
        // If we're already buffering, don't start another preview
        if (tts.isBuffering) {
          shouldPlayWhenBufferCompleteRef.current = true;
          setIsVideoPlaying(false);
          return;
        }

        setIsVideoPlaying(false);
        tts.preview({
          text: currentScriptText,
          voice_id: voiceId,
          project_id: projectId,
          brand_id: brandId,
        });
      } else {
        tts.stop();
        setIsVideoPlaying(false);
        shouldPlayWhenBufferCompleteRef.current = false;
      }
    } else {
      // No TTS - play video normally
      setIsVideoPlaying(isPlaying);
    }
  };

  const onTimeChange = (time: number, percentage: number) => {
    uiInteraction({
      item_name: 'ai_avatar_video_display_progress',
      interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
      interaction_details: `${percentage}%`,
      flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
    });
  };

  useEffect(() => {
    if (isVideoPlaying) {
      setIsVideoPlaying(false);
      tts.stop();
    }
    shouldPlayWhenBufferCompleteRef.current = false;
  }, [currentScriptText]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => tts.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isScriptEdited && isVideoPlaying) {
      setIsVideoPlaying(false);
      tts.stop();
    }
  }, [isScriptEdited, isVideoPlaying, tts]);

  // Track Avatar Alert appearance and video-stopped disappearance
  useEffect(() => {
    if (isAvatarAnimationAlertDismissed) {
      return;
    }
    if (isAvatarAlertVisible) {
      avatarAlertIdsRef.current.presentationId = uuid();
      viewTransitionPresented({
        presentation_id: avatarAlertIdsRef.current.presentationId,
        flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
        view_type: 'alert',
        view_name: 'avatar_animation_alert',
        view_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
        source: 'video_playing',
      });
    } else if (avatarAlertIdsRef.current.presentationId) {
      viewTransitionDismissed({
        presentation_id: avatarAlertIdsRef.current.presentationId,
        interaction_id: avatarAlertIdsRef.current.presentationId,
        flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
        reason: 'video_stopped',
      });
    }
  }, [isAvatarAlertVisible, isAvatarAnimationAlertDismissed]);

  const handleMixerButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAudioMixAnchorEl(event.currentTarget);
      setAudioMixPopperOpen((prev) => !prev);
    },
    []
  );

  const videoDisplayActionComponent = useMemo(() => {
    const mixerButton = (
      <Tooltip title={translate('Adjust Audio Mix')} placement="top" arrow>
        <Box>
          <Button
            appearance="neutral"
            mode="tinted"
            size="medium"
            sx={{padding: '6px 8px'}}
            onClick={handleMixerButtonClick}
          >
            <Icon
              name="Features-Equalizer"
              appearance="neutral"
              size="medium"
              color={designSystemToken('semantic.fg.neutral')}
            />
          </Button>
        </Box>
      </Tooltip>
    );
    const replaceButton = (
      <ReplaceVideoButton
        onClick={(event) => {
          event.stopPropagation();
          const interactionId = uuid();
          uiInteraction({
            item_name: 'replace_video',
            interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
            flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
            interaction_id: interactionId,
          });
          viewPresented({
            interaction_id: interactionId,
            view_name: CONTENT_LAB_VIEW_NAMES.REPLACE_VIDEO_MODAL,
            presentation_id: replaceVideoModalPresentationId,
            view_type: 'popup',
            view_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
            source: 'item_pressed',
          });
          setShowReplaceVideoModal(true);
        }}
      />
    );

    return (
      <Box sx={{display: 'flex', gap: '16px'}}>
        {showAudioMixer ? mixerButton : null}
        {replaceButton}
      </Box>
    );
  }, [handleMixerButtonClick, replaceVideoModalPresentationId, showAudioMixer]);

  return (
    <Box height="100%" position="relative">
      <OverlayAlertContainer width="370px" isVisible={isAvatarAlertVisible}>
        <DismissibleAlert
          severity="info"
          title={translate('Avatar will animate after generation')}
          onClose={() => {
            const interactionId = uuid();
            avatarAlertIdsRef.current.interactionId = interactionId;
            if (avatarAlertIdsRef.current.presentationId) {
              uiInteraction({
                item_name: 'avatar_animation_alert_close',
                interaction_location: CONTENT_LAB_VIEW_NAMES.AI_AVATAR,
                flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
                interaction_id: interactionId,
                presentation_id: avatarAlertIdsRef.current.presentationId,
              });
              viewTransitionDismissed({
                presentation_id: avatarAlertIdsRef.current.presentationId,
                interaction_id: interactionId,
                flow_name: FLOW_NAMES.CONTENT_LAB.AI_AVATAR,
                reason: 'user_dismissed',
              });
            }
            setIsAvatarAnimationAlertDismissed(true);
          }}
        />
      </OverlayAlertContainer>
      <VideoDisplay
        {...videoProps}
        enableKeyboardControls={false}
        onPlayToggle={onPlayToggle}
        onTimeChange={onTimeChange}
        isPlayingState={{
          isPlaying: isVideoPlaying,
          setIsPlaying: setIsVideoPlaying,
        }}
        videoVolume={avatarSpec.backgroundAudioReductionFactor}
        resetOnStop
        disableSeeking
        playingDisabled={tts.isLoading || isScriptEdited}
        showLoader={tts.isLoading}
        cursorStyle={isScriptEdited ? 'default' : 'pointer'}
        allowVolumeControl={false}
        actionComponent={videoDisplayActionComponent}
        renderVideoOverlay={(videoRef: React.RefObject<HTMLVideoElement>) => {
          return selectedAvatarKind
            ? selectedAvatar && (
                <AvatarOverlay
                  selectedAvatarKind={selectedAvatarKind}
                  avatarSpec={avatarSpec}
                  selectedAvatar={selectedAvatar}
                  videoDisplayDimensions={{
                    width: videoRef.current?.clientWidth || 0,
                    height: videoRef.current?.clientHeight || 0,
                  }}
                />
              )
            : null;
        }}
      />
      <OverlayAlertContainer width="560px" isVisible={isScriptEdited}>
        <DismissibleAlert
          severity="info"
          title={translate(
            'Pending script changes, click "Apply" to update the preview'
          )}
        />
      </OverlayAlertContainer>
      <ReplaceVideoModal
        open={showReplaceVideoModal}
        setOpen={setShowReplaceVideoModal}
        presentationId={replaceVideoModalPresentationId}
        setPresentationId={setReplaceVideoModalPresentationId}
        createDropzoneConfig={createDropzoneConfig}
        headlineText={translate(
          'Replacing video will reset your avatar customization.'
        )}
        flowName={FLOW_NAMES.CONTENT_LAB.AI_AVATAR}
        resetProject={() => {
          contentLabActions.removeFeatureUploadingFiles(FeatureName.AI_AVATAR);
          contentLabActions.removeFeatureUploadedVideos(FeatureName.AI_AVATAR);
          contentLabActions.removeFeatureAssetErrors(FeatureName.AI_AVATAR);
          contentLabActions.resetDraftAiAvatarProject();
        }}
      />
      <AudioMixPopper
        open={audioMixPopperOpen}
        anchorEl={audioMixAnchorEl}
        onClose={() => setAudioMixPopperOpen(false)}
        avatarAudioReductionFactor={avatarSpec.avatarAudioReductionFactor}
        backgroundAudioReductionFactor={
          avatarSpec.backgroundAudioReductionFactor
        }
        onAudioMixChange={handleAudioMixChange}
      />
    </Box>
  );
}

export default AiAvatarVideoDisplay;
